import React from 'react';
import {
  Button, TextInput, Select, EmptyState, ErrorState, Alert, Badge, IconButton,
  Checkbox,
} from '@parsina/framework';
import {
  Stethoscope, Sparkles, ChevronDown, Trash2, FileText, Grid3x3, MessageSquare, ClipboardList,
  CheckCircle2, Loader2,
} from 'lucide-react';
import { C, CT } from '../../../constants/theme';
import { LTR_TEXT, autoDir, useTranslation } from '../../../i18n/I18nProvider.jsx';
import VoiceDictateButton from '../../../components/common/VoiceDictateButton.jsx';
import { DiagnosisPaneSkeleton } from '../Visit.skeleton.jsx';
import { dictationSupported } from '../../../hooks/useSpeechToText.js';
import { draftToNotePatch, findingToToothEvent } from '../Visit.shared.js';

// §3.2.3 — تشخیص.
//
//   "Data Derivation: subjective feedback · patient history · uploaded
//    medical/dental imaging (AI-assisted analysis)."
//   "Traceability: Every diagnosis entry must support click-to-reveal
//    functionality to show the supporting clinical data used to derive it."
//
// ── Traceability is the whole point of this pane ───────────────────────────
// A diagnosis is stored WITH its evidence: `VisitDiagnosis.evidence` is a list
// of {kind, detail, ref} written in the same row, in the same call, as the
// diagnosis itself (the BFF puts it in the condition's `meta`, because there is
// no evidence column and inventing one downstream is a migration this repo does
// not own). So the reveal shows what was actually recorded — not a
// reconstruction assembled at read time from whatever happens to be nearby now.
//
// The distinction matters because a reconstruction changes: a note edited next
// week would silently rewrite the basis of a diagnosis made today.
//
// ── AI is a SUGGESTION and never a record ──────────────────────────────────
// `differential` and `diagnoses` are separate lists and are drawn separately.
// Nothing is written until the dentist accepts a suggestion, at which point its
// `supportingEvidence` becomes the recorded evidence and `aiAssisted` is set —
// so the record says where it came from.
//
// The differential COSTS PARSINA CREDITS and is fired only from the button.

const EVIDENCE_ICON = {
  subjective: MessageSquare,
  history: FileText,
  chart: Grid3x3,
  file: FileText,
  order: ClipboardList,
  ai: Sparkles,
};

const CONFIDENCE_TONE = {
  high: { bg: CT.goodSoft, ink: CT.goodInk },
  medium: { bg: CT.warnSoft, ink: CT.warnInk },
  low: { bg: CT.surface2, ink: C.textSecondary },
};

// The closed set clinicalencounter enforces (ALLOWED_VERIFICATION_STATUSES + a DB
// CHECK). There is deliberately no third value: an accepted AI suggestion is a
// working diagnosis (`provisional`) and `aiAssisted` carries where it came from.
const VERIFICATION = ['provisional', 'confirmed'];

/** One recorded diagnosis, with its evidence behind a disclosure. */
function DiagnosisRow({ d, t, formatDate, editable, onRemove, onRevealRef }) {
  const [open, setOpen] = React.useState(false);
  const bodyId = React.useId();

  return (
    <div style={{
      border: `1px solid ${C.border}`, borderRadius: 10,
      background: CT.surface, marginBottom: 8, overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 11px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>{d.display}</span>
            {d.code && (
              <span style={{ fontSize: 11, color: C.textMuted }} {...LTR_TEXT}>{d.code}</span>
            )}
            {d.aiAssisted && (
              <Badge variant="outline">
                <Sparkles size={10} aria-hidden style={{ marginInlineEnd: 3 }} />
                {t('visit.diagnosis.aiAssisted')}
              </Badge>
            )}
          </div>
          <div style={{ fontSize: 11, color: C.textMuted }}>
            {d.verificationStatus ? t(`visit.diagnosis.verification.${d.verificationStatus}`) : ''}
            {d.createdAt ? ` · ${formatDate(d.createdAt, 'numeric')}` : ''}
          </div>
        </div>

        {/* §3.2.3's click-to-reveal. A real disclosure with
            aria-expanded/aria-controls, not a <div onClick>: the evidence
            behind a diagnosis has to be reachable from a keyboard — and a kit
            Button, so it meets the touch floor on the phone surface too. */}
        <Button
          variant="outline"
          className="min-h-11 flex-none"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((o) => !o)}
        >
          {t('visit.diagnosis.evidence', { count: d.evidence.length })}
          <ChevronDown
            size={13}
            aria-hidden
            style={{
              marginInlineStart: 4,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform .15s',
            }}
          />
        </Button>

        {editable && (
          <IconButton
            variant="ghost"
            size={44}
            icon={<Trash2 size={14} />}
            label={t('visit.diagnosis.remove', { name: d.display })}
            onClick={() => onRemove(d.id)}
          />
        )}
      </div>

      {open && (
        <div id={bodyId} style={{
          borderTop: `1px solid ${C.border}`, background: CT.surface2, padding: '9px 11px',
        }}>
          {d.evidence.length === 0 ? (
            // Honest rather than fabricated. A diagnosis recorded before the
            // trail existed, or typed by someone who did not itemise their
            // reasoning, has no evidence — and saying so is the truthful answer.
            <span style={{ fontSize: 12, color: C.textMuted }}>
              {t('visit.diagnosis.noEvidence')}
            </span>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {d.evidence.map((e, i) => {
                const Icon = EVIDENCE_ICON[e.kind] || FileText;
                return (
                  <li
                    key={`${e.kind}-${i}`}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 7,
                      fontSize: 12.5, color: C.textSecondary, lineHeight: 1.8,
                      marginBottom: 5,
                    }}
                  >
                    <Icon size={13} aria-hidden style={{ marginTop: 3, flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>
                      <strong style={{ color: C.textPrimary }}>
                        {t(`visit.diagnosis.evidenceKind.${e.kind}`)}{t('common.punct.colon')}
                      </strong>{' '}
                      {e.detail}
                    </span>
                    {/* A chart reference is actionable: it selects the tooth on
                        the odontogram, which is the point of recording it. */}
                    {e.kind === 'chart' && e.ref && (
                      <Button className="min-h-11" variant="subtle" onClick={() => onRevealRef(e)}>
                        {t('visit.diagnosis.showTooth')}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** The panel's own order for drawing the sections a draft proposes. */
const SOAP_SECTION_ORDER = ['s', 'o', 'a', 'p'];

/**
 * The chart vocabulary in the dentist's language, falling back to the raw
 * value. The dental service's vocabulary is open at the edges (a clinic may
 * stock a material nobody else does), so an unknown key must still render as
 * something readable rather than as a blank row.
 */
const label = (t, kind, value) => {
  if (!value) return '';
  const key = `tooth.${kind}.${value}`;
  const out = t(key);
  return out === key ? value : out;
};

/**
 * یافته‌های دندان‌ها — the per-tooth facts the assistant read out of the
 * narrative, each one a proposal for چارت دندان.
 *
 * This is the third thing one «تکمیل…» produces, and the only one that writes
 * somewhere other than the note. It follows the same suggestion-vs-record line
 * the rest of this pane draws: NOTHING is charted until the dentist ticks it
 * and presses «ثبت در چارت دندان».
 *
 * LOW CONFIDENCE STARTS UNTICKED. The model's hard problem is Persian anatomy
 * → FDI («آسیای اول پایین راست» → 46), and the failure that matters is a
 * finding filed against the WRONG TOOTH — invisible in the chart afterwards,
 * because a charted tooth looks the same whoever put it there. So a row the
 * model inferred rather than heard cannot be recorded by pressing the default
 * action; the dentist has to tick it deliberately.
 */
function ToothFindings({ findings, error, chart, onDismiss }) {
  const { t, formatNumber } = useTranslation();
  const rows = findings?.findings || [];

  // Keyed by index: the same tooth can legitimately carry two findings from one
  // narrative (a crown AND a fracture on 23), so nothing here is unique by FDI.
  const [ticked, setTicked] = React.useState(() => new Set());
  const [recording, setRecording] = React.useState(false);
  const [recorded, setRecorded] = React.useState(false);
  const [failure, setFailure] = React.useState(false);

  // Re-arm for each new read: a fresh set of findings must not inherit the
  // previous run's ticks, and must not still be showing the old confirmation.
  React.useEffect(() => {
    setTicked(new Set(
      rows.map((f, i) => (f.confidence === 'high' ? i : -1)).filter((i) => i >= 0),
    ));
    setRecorded(false);
    setFailure(false);
  }, [findings]);

  if (error === 'unavailable') {
    return (
      <Alert variant="warning" style={{ marginTop: 10 }}>
        {t('visit.soap.teeth.unavailable')}
      </Alert>
    );
  }
  if (!findings || error) return null;
  if (!rows.length) {
    return (
      <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 10 }}>
        {t('visit.soap.teeth.none')}
      </div>
    );
  }

  const toggle = (i) => setTicked((prev) => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });

  const commit = async () => {
    const events = [...ticked]
      .sort((a, b) => a - b)
      .map((i) => findingToToothEvent(rows[i]))
      .filter(Boolean);
    if (!events.length) return;
    setFailure(false);
    setRecording(true);
    try {
      // ONE batch, because recordToothEvents is all-or-nothing: three findings
      // confirmed together are one clinical act, and committing the valid half
      // would leave a chart the dentist never saw.
      const err = await chart.record(events);
      if (err) {
        setFailure(true);
        return;
      }
      setRecorded(true);
    } finally {
      setRecording(false);
    }
  };

  const anyLow = rows.some((f) => f.confidence !== 'high');

  return (
    <div
      data-testid="soap-tooth-findings"
      style={{
        border: `1px dashed ${C.border}`, borderRadius: 10,
        padding: '10px 12px', marginTop: 10, background: CT.surface,
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4,
        fontSize: 11.5, fontWeight: 800, color: C.textSecondary,
      }}>
        <Grid3x3 size={13} aria-hidden />
        {t('visit.soap.teeth.title')}
      </div>
      <div style={{ fontSize: 11.5, color: C.textMuted, lineHeight: 1.8, marginBottom: 8 }}>
        {t('visit.soap.teeth.description')}
      </div>

      {recorded ? (
        <div
          role="status"
          style={{
            display: 'flex', alignItems: 'center', gap: 7,
            background: CT.goodSoft, border: `1px solid ${CT.good}`,
            borderRadius: 8, padding: '7px 10px', marginBottom: 9,
            fontSize: 12.5, fontWeight: 800, color: CT.goodInk,
          }}
        >
          <CheckCircle2 size={15} aria-hidden style={{ flexShrink: 0 }} />
          {t('visit.soap.teeth.recorded')}
        </div>
      ) : (
        <>
          {failure && (
            <Alert variant="error" style={{ marginBottom: 8 }}>
              {t('visit.soap.teeth.recordFailed')}
            </Alert>
          )}
          {anyLow && (
            <div style={{ fontSize: 11, color: CT.warnInk, marginBottom: 8, lineHeight: 1.7 }}>
              {t('visit.soap.teeth.lowConfidenceHint')}
            </div>
          )}
        </>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((f, i) => {
          const on = ticked.has(i);
          return (
            // The kit's Checkbox, whose `label` takes a NODE and wraps the
            // whole row in its own <label> — so the row is the tap target
            // (2.75rem on a phone) without a hand-rolled control.
            <Checkbox
              key={`${f.toothFdi}-${f.aspect}-${f.surface || ''}-${i}`}
              checked={on}
              disabled={recorded || recording}
              onChange={() => toggle(i)}
              className="min-h-11 items-start"
              data-testid="tooth-finding-row"
              data-state={on ? 'checked' : 'unchecked'}
              style={{
                border: `1px solid ${on ? CT.good : C.border}`,
                background: on ? CT.goodSoft : CT.surface2,
                borderRadius: 8, padding: '8px 10px',
              }}
              label={(
                <span style={{ display: 'block', minWidth: 0 }}>
                  <span style={{
                    display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                    fontSize: 12.5, fontWeight: 700, color: C.textPrimary,
                  }}>
                    {/* The FDI number is the one thing that must be checkable
                        at a glance, so it leads and it is LTR — a tooth number
                        is digits whichever way the column reads. */}
                    <span {...LTR_TEXT} style={{ fontWeight: 800 }}>
                      {t('visit.soap.teeth.tooth', { fdi: formatNumber(f.toothFdi) })}
                    </span>
                    <span>{label(t, 'value', f.value)}</span>
                    {f.aspect === 'surface' && f.surface && (
                      <Badge variant="outline">
                        {t('visit.soap.teeth.surface', { surface: f.surface })}
                      </Badge>
                    )}
                    {f.material && <Badge variant="outline">{label(t, 'material', f.material)}</Badge>}
                    {f.isHistorical && <Badge variant="outline">{t('visit.soap.teeth.historical')}</Badge>}
                    <span style={{
                      fontSize: 11,
                      color: f.confidence === 'high' ? CT.goodInk : CT.warnInk,
                    }}>
                      {t(`visit.soap.teeth.confidence.${f.confidence}`)}
                    </span>
                  </span>
                  {/* The dentist's own sentence, so the reading can be checked
                      against what was actually said rather than trusted. */}
                  {f.evidence && (
                    <span style={{
                      display: 'block', fontSize: 11.5, color: C.textMuted,
                      lineHeight: 1.7, marginTop: 3,
                    }}>
                      «{f.evidence}»
                    </span>
                  )}
                </span>
              )}
            />
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        {!recorded && (
          <Button
            className="min-h-11"
            onClick={commit}
            disabled={recording || chart?.saving || ticked.size === 0}
          >
            {recording
              ? t('visit.soap.teeth.recording')
              : t('visit.soap.teeth.record', { count: formatNumber(ticked.size) })}
          </Button>
        )}
        <Button className="min-h-11" variant="outline" onClick={onDismiss} disabled={recording}>
          {t('visit.soap.dismiss')}
        </Button>
      </div>
    </div>
  );
}

/**
 * دستیار شرح حال — the doctor app's SOAP flow, on dental terms.
 *
 * The dentist TALKS. There is no typed narrative any more — 2026-08-26 removed
 * the textarea and the «تکمیل شرح حال و دریافت توصیه» button both, because the
 * flow they described was three presses for one intention: dictate, watch the
 * box fill, then press a second button to send what was just said. Now one
 * labelled voice button is the whole input: while it records the words stream
 * in as a read-only preview, and the moment the final transcript arrives the
 * analysis fires BY ITSELF — pressing stop IS pressing تکمیل, and the stop
 * button's own label says so («پایان و دریافت توصیه»).
 *
 * Two things follow from auto-firing, and both are guarded here:
 *   * AN EMPTY TRANSCRIPT MUST NOT ANALYSE. All three analysis calls bill
 *     credits; a silent recording already cost its transcription minute, and
 *     auto-sending its empty result would buy three refusals on top.
 *   * A FAILURE MUST NOT COST THE SPEECH. With no box holding the narrative, a
 *     transient refine failure would otherwise mean dictating the whole visit
 *     again — so the heard transcript is kept and «ارسال دوباره» resends it
 *     without a second recording (or a second transcription charge).
 *
 * Two things come back — a draft of the note's sections, and the assistant's
 * advice. Neither writes anything: the draft lands in شرح حال بیمار only
 * through «اعمال», and a suggested diagnosis becomes a record only through
 * «ثبت» — the same suggestion-vs-record line the differential below draws.
 *
 * S IS NEVER REPLACED. The dental subjective is structured (شکایت اصلی، شدت
 * درد، دندان‌ها…) and a prose merge cannot round-trip it, so the S section
 * contributes only its additions, appended under یادداشت — exactly how پرسش‌ها
 * records a confirmed finding. O/A/P are replaced wholesale: nothing else in
 * this app writes those three fields.
 */
function SoapAssistant({ soap, note, chart, alreadyRecorded, saving, onRecord }) {
  const { t } = useTranslation();
  const {
    analyzing, draft, advice, findings, refineError, adviceError, findingsError,
    analyze, clearDraft, clearAdvice, clearFindings,
  } = soap;

  // `live` is the words the transcription stream has produced for a session
  // that is still running — a read-only preview, since there is nothing
  // editable for them to land in any more. `heard` is the final transcript of
  // the last session: what the analysis was fired with, kept so a failed
  // analysis can be resent without making the dentist dictate the visit again.
  const [live, setLive] = React.useState('');
  const [heard, setHeard] = React.useState('');
  const [applying, setApplying] = React.useState(false);
  const [applied, setApplied] = React.useState(false);
  const [failure, setFailure] = React.useState(null);

  const changedSections = (draft?.sections || [])
    .filter((s) => s.changed)
    .sort((a, b) => SOAP_SECTION_ORDER.indexOf(a.section) - SOAP_SECTION_ORDER.indexOf(b.section));

  /** The stop press, one step later: the final transcript IS the submission. */
  const run = (text) => {
    const clean = (text || '').trim();
    // A silent recording produces no final text and VoiceDictateButton never
    // calls onText — this guard is for `run(heard)` from «ارسال دوباره» and
    // for defence in depth: analysis bills three times, silence must not.
    if (!clean) return;
    setHeard(clean);
    setApplied(false);
    setFailure(null);
    analyze(clean);
  };

  const apply = async () => {
    setFailure(null);
    setApplying(true);
    try {
      const patch = draftToNotePatch(draft, note?.note?.dental?.notes || '');
      const err = await note.save(patch);
      if (err) {
        setFailure(err);
        return;
      }
      setApplied(true);
      clearDraft();
    } finally {
      setApplying(false);
    }
  };

  const recordSuggested = async () => {
    setFailure(null);
    const err = await onRecord({
      code: advice.icdCodes?.[0]?.code || '',
      display: advice.suggestedDiagnosis,
      verificationStatus: 'provisional',
      evidence: [
        // The assistant's own account of what it reasoned from — the summary
        // is the trail, the same way a differential's supportingEvidence is.
        { kind: 'ai', detail: t('visit.soap.aiEvidence'), ref: null },
        ...(advice.summary ? [{ kind: 'ai', detail: advice.summary.split('\n')[0], ref: null }] : []),
      ],
      aiAssisted: true,
    });
    if (err) setFailure(err);
  };

  const dxTaken = advice?.suggestedDiagnosis
    && alreadyRecorded.has(advice.suggestedDiagnosis.trim());

  return (
    <div
      data-testid="soap-assistant"
      style={{
        border: `1px solid ${C.border}`, borderRadius: 10,
        padding: 12, marginBottom: 14, background: CT.surface2,
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4,
        fontSize: 12, fontWeight: 800, color: C.textSecondary,
      }}>
        <Sparkles size={13} aria-hidden />
        {t('visit.soap.title')}
      </div>
      <div style={{ fontSize: 11.5, color: C.textMuted, lineHeight: 1.8, marginBottom: 8 }}>
        {t('visit.soap.description')}
      </div>

      {/* ── ONE BUTTON IS THE WHOLE INPUT ─────────────────────────────────
          The textarea and the «تکمیل…» button are gone (see the header note).
          Speaking is the form: the wide appearance carries the explanation the
          field's placeholder used to, and `stopLabel` carries the promise the
          removed button used to — stopping is what fires the analysis.

          `continuous`: the narrative is a paragraph, and a recogniser that
          closes at the first breath has the dentist pressing the mic once per
          sentence — the same reasoning as یادداشت's mic. */}
      {dictationSupported() ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <VoiceDictateButton
            continuous
            appearance="wide"
            disabled={analyzing}
            label={t('visit.soap.speak')}
            stopLabel={t('visit.soap.speakStop')}
            onInterim={setLive}
            onText={run}
          />
          {analyzing && (
            <span
              role="status"
              aria-live="polite"
              data-testid="soap-analyzing"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 12, color: C.textSecondary, whiteSpace: 'nowrap',
              }}
            >
              <Loader2 size={13} aria-hidden className="animate-spin" />
              {t('visit.soap.analyzing')}
            </span>
          )}
        </div>
      ) : (
        // The mic beside شکایت اصلی hides itself where dictation cannot work,
        // because typing carries that field either way. Here dictation IS the
        // feature, so its absence has to be said rather than shown as an
        // inexplicably empty panel.
        <div style={{ fontSize: 12, color: C.textMuted }}>
          {t('visit.soap.voiceUnavailable')}
        </div>
      )}

      {/* The stream, while the dentist is still talking — the box this used to
          fill is gone, so the preview renders as plain text. The service
          transcribes on a timer (~10–15s per hint), so this appears in jumps,
          and the pulsing dot beside the button is the feedback before the
          first jump. Superseded wholesale by the final transcript. */}
      {!!live && (
        <div
          data-testid="soap-live-transcript"
          dir={autoDir(live)}
          style={{
            marginTop: 8, padding: '8px 10px', borderRadius: 8,
            border: `1px dashed ${C.border}`, background: CT.surface1,
            fontSize: 12.5, lineHeight: 1.9, color: C.textSecondary,
          }}
        >
          {live}
        </div>
      )}

      {/* What the analysis was fired with — shown while it runs, and kept on a
          failure so «ارسال دوباره» can resend the words instead of the dentist
          re-dictating them. Dropped once a draft or advice stands, which
          restate the content in better form. */}
      {!!heard && !live && (analyzing || refineError || adviceError) && (
        <div
          data-testid="soap-heard"
          dir={autoDir(heard)}
          style={{
            marginTop: 8, padding: '8px 10px', borderRadius: 8,
            border: `1px dashed ${C.border}`, background: CT.surface1,
            fontSize: 12.5, lineHeight: 1.9, color: C.textSecondary,
          }}
        >
          <span style={{ fontWeight: 700, marginInlineEnd: 6 }}>{t('visit.soap.heard')}</span>
          {heard}
          {!analyzing && (
            <div style={{ marginTop: 6 }}>
              <Button className="min-h-11" variant="outline" onClick={() => run(heard)}>
                <Sparkles size={13} aria-hidden style={{ marginInlineEnd: 5 }} />
                {t('visit.soap.retry')}
              </Button>
            </div>
          )}
        </div>
      )}

      <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6 }}>
        {t('visit.ai.costsCredits')}
      </div>

      {failure && (
        <Alert variant="error" style={{ marginTop: 10 }}>
          {failure.message || t('visit.soap.applyFailed')}
        </Alert>
      )}
      {(refineError === 'credits' || adviceError === 'credits') && (
        <Alert variant="info" title={t('errors.credits.title')} style={{ marginTop: 10 }}>
          {t('errors.credits.body')}
        </Alert>
      )}
      {refineError === 'unavailable' && (
        <Alert variant="warning" style={{ marginTop: 10 }}>
          {t('visit.soap.refineUnavailable')}
        </Alert>
      )}
      {applied && (
        <Alert variant="success" style={{ marginTop: 10 }}>
          {t('visit.soap.applied')}
        </Alert>
      )}

      {/* ── The draft — a PROPOSAL, dashed like the differential's ────────── */}
      {draft && !refineError && (
        changedSections.length === 0 ? (
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 10 }}>
            {t('visit.soap.noNews')}
          </div>
        ) : (
          <div
            data-testid="soap-draft"
            style={{
              border: `1px dashed ${C.border}`, borderRadius: 10,
              padding: '10px 12px', marginTop: 10, background: CT.surface,
            }}
          >
            <div style={{ fontSize: 11.5, fontWeight: 800, color: C.textSecondary, marginBottom: 6 }}>
              {t('visit.soap.draftTitle')}
            </div>
            {changedSections.map((s) => (
              <div key={s.section} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.textPrimary, marginBottom: 2 }}>
                  {t(`visit.soap.sections.${s.section}`)}
                </div>
                <div style={{ fontSize: 12.5, color: C.textSecondary, lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>
                  {s.text}
                </div>
                {s.additions.length > 0 && (
                  <div style={{ marginTop: 3, fontSize: 12, lineHeight: 1.8 }}>
                    {s.additions.map((a, i) => (
                      <div key={i} style={{ color: CT.goodInk }}>+ {a}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8 }}>
              <Button className="min-h-11" onClick={apply} disabled={applying}>
                {applying ? t('common.actions.saving') : t('visit.soap.apply')}
              </Button>
              <Button className="min-h-11" variant="outline" onClick={clearDraft} disabled={applying}>
                {t('visit.soap.dismiss')}
              </Button>
            </div>
          </div>
        )
      )}

      {/* ── The advice — display, with the one write behind «ثبت» ─────────── */}
      {adviceError === 'unavailable' && (
        <Alert variant="warning" style={{ marginTop: 10 }}>
          {t('visit.soap.adviceUnavailable')}
        </Alert>
      )}
      {advice && !adviceError && (
        <div
          data-testid="soap-advice"
          data-accepted={dxTaken ? 'true' : 'false'}
          style={{
            // ACCEPTED LOOKS DIFFERENT AT A GLANCE. A dashed border is this
            // pane's mark for "a proposal"; once the dentist has taken the
            // diagnosis into the record it is no longer one, so the card goes
            // solid and green rather than merely disabling its button — the
            // same distinction the record below draws against the suggestions.
            border: dxTaken ? `1.5px solid ${CT.good}` : `1px dashed ${C.border}`,
            borderRadius: 10,
            padding: '10px 12px', marginTop: 10,
            background: dxTaken ? CT.goodSoft : CT.surface,
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6,
            fontSize: 11.5, fontWeight: 800, color: C.textSecondary,
          }}>
            <Sparkles size={13} aria-hidden />
            {t('visit.soap.adviceTitle')}
          </div>

          {/* The banner, above everything: a dentist scrolling past should be
              able to tell this was acted on without reading the button. */}
          {dxTaken && (
            <div
              role="status"
              style={{
                display: 'flex', alignItems: 'center', gap: 7,
                background: CT.surface, border: `1px solid ${CT.good}`,
                borderRadius: 8, padding: '7px 10px', marginBottom: 9,
                fontSize: 12.5, fontWeight: 800, color: CT.goodInk,
              }}
            >
              <CheckCircle2 size={15} aria-hidden style={{ flexShrink: 0 }} />
              {t('visit.soap.accepted')}
            </div>
          )}

          {advice.summary && (
            <div style={{ fontSize: 12.5, color: C.textSecondary, lineHeight: 1.9, whiteSpace: 'pre-wrap', marginBottom: 8 }}>
              {advice.summary}
            </div>
          )}

          {advice.suggestedDiagnosis && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              {dxTaken && <CheckCircle2 size={14} aria-hidden style={{ color: CT.good, flexShrink: 0 }} />}
              <span style={{
                fontSize: 12, fontWeight: 700,
                color: dxTaken ? CT.goodInk : C.textPrimary,
              }}>
                {t('visit.soap.suggestedDiagnosis')}{t('common.punct.colon')} {advice.suggestedDiagnosis}
              </span>
              {advice.icdCodes.map((c) => (
                <span
                  key={c.code}
                  style={{ fontSize: 11, color: dxTaken ? CT.goodInk : C.textMuted }}
                  {...LTR_TEXT}
                  title={c.description}
                >
                  {c.code}
                </span>
              ))}
              {/* Once recorded the control is GONE, not greyed: a disabled
                  button still reads as an action the dentist might be able to
                  take, and there is nothing left to take here. */}
              {dxTaken ? (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: CT.good, color: '#fff', borderRadius: 8,
                  padding: '3px 9px', fontSize: 11, fontWeight: 800,
                }}>
                  <CheckCircle2 size={11} aria-hidden />
                  {t('visit.diagnosis.alreadyRecorded')}
                </span>
              ) : (
                <Button
                  className="min-h-11"
                  variant="subtle"
                  onClick={recordSuggested}
                  disabled={saving}
                >
                  {t('visit.soap.recordDiagnosis')}
                </Button>
              )}
            </div>
          )}

          {advice.suggestedActions.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.textPrimary, marginBottom: 2 }}>
                {t('visit.soap.actions')}
              </div>
              {advice.suggestedActions.map((a, i) => (
                <div key={i} style={{ fontSize: 12.5, color: C.textSecondary, lineHeight: 1.8 }}>• {a}</div>
              ))}
            </div>
          )}

          {advice.suggestedMedications.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.textPrimary, marginBottom: 2 }}>
                {t('visit.soap.medications')}
              </div>
              {advice.suggestedMedications.map((m, i) => (
                <div key={i} style={{ fontSize: 12.5, color: C.textSecondary, lineHeight: 1.8 }}>• {m}</div>
              ))}
            </div>
          )}

          <Button className="min-h-11" variant="outline" onClick={clearAdvice}>
            {t('visit.soap.dismiss')}
          </Button>
        </div>
      )}

      {/* ── The teeth — the one part of this panel that writes to the chart ── */}
      <ToothFindings
        findings={findings}
        error={findingsError}
        chart={chart}
        onDismiss={clearFindings}
      />
    </div>
  );
}

export default function DiagnosisPane({
  diagnoses, editable, note, selectedTooth, onSelectTooth, files, soap, chart,
}) {
  const { t, formatNumber, formatDate } = useTranslation();
  const {
    diagnoses: rows, loading, error, saving, record, remove,
    differential, suggesting, suggestError, suggest, reload,
  } = diagnoses;

  const [draft, setDraft] = React.useState({ code: '', display: '', verificationStatus: 'provisional' });
  const [failure, setFailure] = React.useState(null);

  /**
   * The evidence a MANUALLY typed diagnosis rests on.
   *
   * Assembled from what is actually on screen at the moment of recording — the
   * subjective the dentist wrote, the tooth they have selected, the reports
   * extracted from this visit's files. That is §3.2.3's three derivation
   * sources, captured at the time rather than re-derived later.
   */
  const currentEvidence = React.useCallback(() => {
    const out = [];
    const subjective = note?.note?.subjective;
    if (subjective) {
      out.push({ kind: 'subjective', detail: subjective.split('\n')[0], ref: null });
    }
    if (selectedTooth) {
      out.push({
        kind: 'chart',
        detail: t('visit.diagnosis.toothEvidence', { fdi: formatNumber(selectedTooth) }),
        ref: selectedTooth,
      });
    }
    for (const f of (files || []).filter((x) => x.fromThisVisit && x.reportText)) {
      out.push({ kind: 'file', detail: `${f.fileName}: ${f.reportText.split('\n')[0]}`, ref: f.id });
    }
    return out;
  }, [note, selectedTooth, files, t, formatNumber]);

  const submit = async () => {
    setFailure(null);
    if (!draft.display.trim()) return;
    const err = await record({
      code: draft.code.trim(),
      display: draft.display.trim(),
      verificationStatus: draft.verificationStatus,
      evidence: currentEvidence(),
      aiAssisted: false,
    });
    if (err) {
      setFailure(err);
      return;
    }
    setDraft({ code: '', display: '', verificationStatus: 'provisional' });
  };

  /** Accept a suggestion: its own evidence becomes the recorded evidence. */
  const accept = async (item) => {
    setFailure(null);
    const err = await record({
      code: item.diagnosisCode || '',
      display: item.diagnosisName,
      // `provisional`, not `differential`: the service accepts only the closed
      // provisional/confirmed set, and an accepted suggestion the dentist has
      // not yet established is exactly a working diagnosis.
      verificationStatus: 'provisional',
      evidence: [
        // The service's own account of what it reasoned from — this is what
        // makes an accepted suggestion auditable a month later.
        ...item.supportingEvidence.map((detail) => ({ kind: 'ai', detail, ref: null })),
        ...currentEvidence(),
      ],
      aiAssisted: true,
    });
    if (err) setFailure(err);
  };

  const drop = async (id) => {
    setFailure(null);
    const err = await remove(id);
    if (err) setFailure(err);
  };

  // The pane's own shape — the add form and the diagnosis rows — never a
  // centred spinner (DESIGN.md §"Loading & skeleton").
  if (loading) {
    return <DiagnosisPaneSkeleton label={t('common.loading')} />;
  }

  if (error) {
    return (
      <div role="alert">
        <ErrorState
          title={t('visit.diagnosis.error.title')}
          description={t('visit.diagnosis.error.body')}
          onRetry={reload}
          retryLabel={t('common.actions.retry')}
        />
      </div>
    );
  }

  const alreadyRecorded = new Set(rows.map((r) => (r.display || '').trim()));

  return (
    <div data-testid="visit-diagnosis" data-state={rows.length ? 'content' : 'empty'}>
      {failure && (
        <Alert variant="error" style={{ marginBottom: 12 }}>
          {failure.message || t('visit.diagnosis.saveFailed')}
        </Alert>
      )}

      {/* دستیار شرح حال first: the narrative comes before the conclusion, the
          way the visit itself does. Editable-only — its whole output is writes
          this pane refuses on a finished visit. */}
      {editable && soap && (
        <SoapAssistant
          soap={soap}
          note={note}
          chart={chart}
          alreadyRecorded={alreadyRecorded}
          saving={saving}
          onRecord={record}
        />
      )}

      {editable ? (
        <div style={{
          border: `1px solid ${C.border}`, borderRadius: 10,
          padding: 12, marginBottom: 14, background: CT.surface2,
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)', gap: 10,
          }}>
            <TextInput
              label={t('visit.diagnosis.name')}
              aria-label={t('visit.diagnosis.name')}
              dir="auto"
              value={draft.display}
              onChange={(e) => setDraft((d) => ({ ...d, display: e.target.value }))}
            />
            <TextInput
              label={t('visit.diagnosis.code')}
              aria-label={t('visit.diagnosis.code')}
              dir="ltr"
              value={draft.code}
              placeholder="K02.1"
              onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))}
            />
            <Select
              label={t('visit.diagnosis.certainty')}
              aria-label={t('visit.diagnosis.certainty')}
              placeholder=""
              value={draft.verificationStatus}
              onChange={(e) => setDraft((d) => ({ ...d, verificationStatus: e.target.value }))}
              options={VERIFICATION.map((v) => ({
                value: v, label: t(`visit.diagnosis.verification.${v}`),
              }))}
            />
          </div>

          {/* What will be attached, shown BEFORE the write. A trail the dentist
              cannot see before it is stored is a trail they cannot correct. */}
          <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 8, lineHeight: 1.8 }}>
            {t('visit.diagnosis.willAttach', { count: formatNumber(currentEvidence().length) })}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <Button className="min-h-11" onClick={submit} disabled={saving || !draft.display.trim()}>
              {saving ? t('common.actions.saving') : t('visit.diagnosis.add')}
            </Button>
            {/* COSTS PARSINA CREDITS — a button, never automatic. */}
            <Button className="min-h-11" variant="outline" onClick={suggest} disabled={suggesting}>
              <Sparkles size={14} aria-hidden style={{ marginInlineEnd: 5 }} />
              {suggesting ? t('visit.diagnosis.suggesting') : t('visit.diagnosis.suggest')}
            </Button>
          </div>
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6 }}>
            {t('visit.ai.costsCredits')}
          </div>
        </div>
      ) : (
        <Alert variant="info" style={{ marginBottom: 12 }}>{t('visit.readOnly.note')}</Alert>
      )}

      {/* The AI differential — SUGGESTIONS, drawn apart from the record so the
          two can never be mistaken for each other. */}
      {suggestError === 'credits' && (
        <Alert variant="info" title={t('errors.credits.title')} style={{ marginBottom: 12 }}>
          {t('errors.credits.body')}
        </Alert>
      )}
      {suggestError === 'unavailable' && (
        <Alert variant="warning" style={{ marginBottom: 12 }}>
          {t('visit.diagnosis.suggestUnavailable')}
        </Alert>
      )}

      {differential !== null && !suggestError && (
        <div style={{ marginBottom: 14 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8,
            fontSize: 11.5, fontWeight: 800, color: C.textSecondary,
          }}>
            <Sparkles size={13} aria-hidden />
            {t('visit.diagnosis.suggestions')}
          </div>
          {differential.length === 0 ? (
            <span style={{ fontSize: 12, color: C.textMuted }}>
              {t('visit.diagnosis.noSuggestions')}
            </span>
          ) : (
            differential.map((item) => {
              const tone = CONFIDENCE_TONE[item.confidence] || CONFIDENCE_TONE.low;
              const taken = alreadyRecorded.has((item.diagnosisName || '').trim());
              return (
                <div
                  key={`${item.diagnosisCode}-${item.diagnosisName}`}
                  style={{
                    border: `1px dashed ${C.border}`, borderRadius: 10,
                    padding: '9px 11px', marginBottom: 8, background: CT.surface,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>
                        {item.diagnosisName}
                      </span>
                      {item.diagnosisCode && (
                        <span style={{ fontSize: 11, color: C.textMuted, marginInlineStart: 6 }} {...LTR_TEXT}>
                          {item.diagnosisCode}
                        </span>
                      )}
                    </div>
                    {item.confidence && (
                      <span style={{
                        background: tone.bg, color: tone.ink, borderRadius: 8,
                        padding: '2px 8px', fontSize: 11, fontWeight: 700,
                      }}>
                        {t(`visit.diagnosis.confidence.${item.confidence}`)}
                      </span>
                    )}
                    {editable && (
                      <Button className="min-h-11" variant="subtle" onClick={() => accept(item)} disabled={saving || taken}>
                        {taken ? t('visit.diagnosis.alreadyRecorded') : t('visit.diagnosis.accept')}
                      </Button>
                    )}
                  </div>

                  {/* The suggestion's OWN evidence, visible before it is
                      accepted: §3.2.3's traceability applies to a proposal at
                      least as much as to a record. */}
                  {(item.supportingEvidence.length > 0 || item.contradictingEvidence.length > 0) && (
                    <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.8 }}>
                      {item.supportingEvidence.map((e, i) => (
                        <div key={`s${i}`} style={{ color: CT.goodInk }}>+ {e}</div>
                      ))}
                      {item.contradictingEvidence.map((e, i) => (
                        <div key={`c${i}`} style={{ color: C.textMuted }}>− {e}</div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* The record. */}
      {rows.length === 0 ? (
        <EmptyState
          icon={<Stethoscope size={40} color={C.textMuted} />}
          title={t('visit.diagnosis.empty.title')}
          description={t('visit.diagnosis.empty.description')}
        />
      ) : (
        rows.map((d) => (
          <DiagnosisRow
            key={d.id}
            d={d}
            t={t}
            formatDate={formatDate}
            editable={editable}
            onRemove={drop}
            onRevealRef={(e) => onSelectTooth(e.ref)}
          />
        ))
      )}
    </div>
  );
}
